/**
 * Expert-review hub — data + which visit to show.
 *
 * Story for this folder:
 *   1. Loading → HubPageSkeleton (card may swipe)
 *   2. Submitted visit → HubSubmittedScreen (status firstPlanDropTime is a number; final frame, no springs)
 *   3. Pending visit → HubInteractiveScreen (swipe once if skeleton did not; Framer on submit)
 *
 * Visit vs submit phase:
 *   • HubVisit = whole page for this open (pending | submitted) — set once when status is ready
 *   • HubSubmitPhase = CTA inside pending visit only (pending → submitting → submitted)
 * Visit must not flip when status becomes done mid-submit (would remount and kill motion).
 *
 * Motion rule:
 *   • CSS DirectionalSlideSwap = first paint of the pending-visit card only
 *   • Framer = submit only, inside pending visit, after that CSS swipe finishes
 *
 * Belongs here: status/user loading, one-shot visit choice, summary wiring, set risk profile from status.
 * Does not belong here: layout markup or card chrome.
 */
import { useEffect, useRef } from 'react'
import BackButton from '../../../../components/ui-components/BackButton'
import { getBottomPadding } from '../../../../utils/sessionStorage'
import { EventNames } from '../../../../types/misc.types'
import { lightImpactVibrate, trackEvent } from '../../../../utils/appBridge'
import { ExpertReviewEventIds, ExpertReviewScreen } from '../analytics/expertReviewEvents'
import { expertReviewSafeAreaTopStyle } from '../shared/expertReviewSafeArea'
import { useUserDetails } from '../../../../hooks/useUserDetails'
import { useStrategyQuestionnaireContext } from '../strategy-questionnaire/flow-engine/StrategyQuestionnaireProvider'
import { usePreferencesContext } from '../preferences/flow-engine/PreferencesProvider'
import {
  firstPlanDropTimeFromStatus,
  useExpertReviewStatusContext
} from '../providers/ExpertReviewStatusContext'
import { useExpertReviewNavigation } from '../navigation/expertReviewNavigation'
import { HubPageSkeleton } from './HubPageSkeleton'
import { HubInteractiveScreen } from './HubInteractiveScreen'
import { HubSubmittedScreen } from './HubSubmittedScreen'

/** Page for this open — set once after status is ready. Not the CTA phase (see HubSubmitPhase). */
enum HubVisit {
  Pending = 'pending',
  Submitted = 'submitted'
}

export default function ExpertReviewHubPage() {
  const { statusData, isStatusLoading, isStatusError } = useExpertReviewStatusContext()
  const { exitExpertReview } = useExpertReviewNavigation()
  const firstPlanDropTime = firstPlanDropTimeFromStatus(statusData)
  const isStatusSubmitted = firstPlanDropTime !== null
  const { setRiskProfileFromStatus, clearSession: clearStrategySession } =
    useStrategyQuestionnaireContext()
  const { clearSession: clearPreferencesSession } = usePreferencesContext()
  const { userDetails, isLoading: isUserLoading } = useUserDetails()
  // If skeleton ran this visit, the loaded card must not swipe again.
  const sawSkeletonRef = useRef(false)
  // Choose once when status is ready — never flip pending↔submitted mid-session
  const hubVisitRef = useRef<HubVisit | null>(null)

  useEffect(() => {
    // Always sync — clear local profile when status has none (avoid stale name on hub)
    setRiskProfileFromStatus(statusData?.strategyQuestionnaire?.riskProfile ?? null)
  }, [statusData?.strategyQuestionnaire?.riskProfile, setRiskProfileFromStatus])

  /** Hub is feature root — clear sessions and leave to portfolio. */
  function handleExitExpertReview() {
    clearStrategySession()
    clearPreferencesSession()
    exitExpertReview()
  }

  // Submitted visit: don't wait on user name — pending skeleton would flash over the timeline
  if (isStatusLoading || (isUserLoading && !isStatusSubmitted)) {
    sawSkeletonRef.current = true
    return <HubPageSkeleton onBack={handleExitExpertReview} />
  }

  if (isStatusError) {
    return (
      <div
        style={{
          ...expertReviewSafeAreaTopStyle(),
          paddingBottom: `calc(${getBottomPadding()}px + 16px)`
        }}
        className="h-screen bg-white px-5 flex flex-col"
      >
        <header className="shrink-0">
          <BackButton
            testLocator="allocation-plan-error-back"
            onClick={() => {
              lightImpactVibrate()
              trackEvent({
                eventName: EventNames.UserClicked,
                payload: {
                  ScreenName: ExpertReviewScreen.Hub,
                  eventId: ExpertReviewEventIds.Back,
                  buttonLabel: 'Back'
                }
              })
              handleExitExpertReview()
            }}
          />
        </header>
        <div className="flex-1 flex flex-col items-center justify-center gap-3">
          <p className="text-gray-600">Failed to load plan status.</p>
        </div>
      </div>
    )
  }

  if (hubVisitRef.current === null) {
    hubVisitRef.current = isStatusSubmitted ? HubVisit.Submitted : HubVisit.Pending
  }

  const summary = {
    userName: userDetails?.name?.trim() ?? '',
    portfolioValue: statusData?.currentPortfolioValue ?? 0,
    fundCount: statusData?.currentActiveFundCount ?? 0
  }

  if (hubVisitRef.current === HubVisit.Submitted) {
    return (
      <HubSubmittedScreen
        summary={summary}
        firstPlanDropTime={firstPlanDropTime}
        onDone={handleExitExpertReview}
      />
    )
  }

  return (
    <HubInteractiveScreen
      shouldSwipeCardIn={!sawSkeletonRef.current}
      summary={summary}
      statusData={statusData}
      onBack={handleExitExpertReview}
      onDone={handleExitExpertReview}
    />
  )
}
